import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BackButton } from '../../components/BackButton';
import { FormPageShell } from '../../components/DataTablePanel';
import { FinancialLinesEditor } from '../../components/FinancialLinesEditor';
import {
  createEmptyAllocation,
  EditableAllocation,
  PaymentAllocationsEditor,
} from '../../components/PaymentAllocationsEditor';
import {
  Button,
  Callout,
  Card,
  DateInput,
  Label,
  LinkChainIcon,
  LoadingSpinner,
  SaveIcon,
  Select,
  Switch,
  Textarea,
  XIcon,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { CustomerLookupItem } from '../../types/customer';
import { InvoiceListItem } from '../../types/invoice';
import {
  EditableFinancialLine,
  amountToApiString,
  createEmptyLine,
  parseAmountInput,
  sumEditableLines,
} from '../../utils/financialLines';
import { formatMoney } from '../../utils/format';

type FormState = {
  customerId: string;
  paymentDate: string;
  description: string;
  notes: string;
  lines: EditableFinancialLine[];
  allocations: EditableAllocation[];
  allowOverpayment: boolean;
};

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function emptyForm(): FormState {
  return {
    customerId: '',
    paymentDate: todayIso(),
    description: '',
    notes: '',
    lines: [createEmptyLine('INVOICE')],
    allocations: [],
    allowOverpayment: false,
  };
}

function moneyToInput(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '';
  return n.toFixed(2).replace('.', ',');
}

export function PaymentFormPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const labels = t().payments;

  const presetCustomerId = searchParams.get('customerId') || '';
  const presetInvoiceId = searchParams.get('invoiceId') || '';

  const [form, setForm] = useState<FormState>(emptyForm);
  const [customers, setCustomers] = useState<CustomerLookupItem[]>([]);
  const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lineTotals = useMemo(
    () => sumEditableLines(form.lines),
    [form.lines],
  );

  const allocationTotals = useMemo(() => {
    let invoice = 0;
    let cash = 0;
    for (const row of form.allocations) {
      invoice += parseAmountInput(row.invoiceAmount) || 0;
      cash += parseAmountInput(row.cashAmount) || 0;
    }
    return { invoice, cash };
  }, [form.allocations]);

  const allocationExceedsPayment =
    allocationTotals.invoice > lineTotals.invoice + 0.0001 ||
    allocationTotals.cash > lineTotals.cash + 0.0001;

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      setLoading(true);
      try {
        const customerResult = await api.getCustomerLookup({
          active: 'true',
        });
        if (cancelled) return;
        setCustomers(customerResult);

        if (isEdit && id) {
          const payment = await api.getPayment(Number(id));
          if (cancelled) return;
          if (
            !customerResult.some((customer) => customer.id === payment.customer.id)
          ) {
            setCustomers([
              ...customerResult,
              {
                id: payment.customer.id,
                name: payment.customer.name,
                active: true,
                creditDays: 0,
              },
            ]);
          }
          setForm({
            customerId: String(payment.customer.id),
            paymentDate: payment.paymentDate.slice(0, 10),
            description: payment.description ?? '',
            notes: payment.notes ?? '',
            lines: payment.lines.map((line) => ({
              key: `line-${line.id}`,
              type: line.type,
              amount: line.amount,
              description: line.description ?? '',
            })),
            allocations: payment.allocations.map((row) => ({
              key: `alloc-${row.id ?? row.invoiceId}`,
              invoiceId: String(row.invoiceId),
              invoiceAmount: row.invoiceAmount,
              cashAmount: row.cashAmount,
            })),
            allowOverpayment: false,
          });
          const invoiceResult = await api.getInvoices({
            customerId: payment.customer.id,
            pageSize: 100,
          });
          if (!cancelled) setInvoices(invoiceResult.data);
        } else if (presetCustomerId) {
          const customerIdNum = Number(presetCustomerId);
          const invoiceResult = await api.getInvoices({
            customerId: customerIdNum,
            pageSize: 100,
          });
          if (cancelled) return;
          setInvoices(invoiceResult.data);

          let invoiceAmount = '';
          let cashAmount = '';
          let invoiceNo = '';

          if (presetInvoiceId) {
            const invoiceIdNum = Number(presetInvoiceId);
            const fromList = invoiceResult.data.find(
              (inv) => inv.id === invoiceIdNum,
            );
            if (fromList) {
              invoiceAmount = moneyToInput(fromList.outstanding.invoice);
              cashAmount = moneyToInput(fromList.outstanding.cash);
              invoiceNo = fromList.invoiceNo;
            } else {
              const ctx = await api.getInvoicePaymentContext(invoiceIdNum);
              if (cancelled) return;
              invoiceAmount = moneyToInput(ctx.remaining.invoice);
              cashAmount = moneyToInput(ctx.remaining.cash);
              invoiceNo = ctx.invoiceNo;
            }
          }

          const lines: EditableFinancialLine[] = [];
          if (invoiceAmount) {
            lines.push({
              ...createEmptyLine('INVOICE'),
              amount: invoiceAmount,
              description: invoiceNo
                ? `${labels.invoice} ${invoiceNo}`
                : '',
            });
          }
          if (cashAmount) {
            lines.push({
              ...createEmptyLine('CASH'),
              amount: cashAmount,
              description: invoiceNo
                ? `${labels.invoice} ${invoiceNo}`
                : '',
            });
          }
          if (lines.length === 0) {
            lines.push(createEmptyLine('INVOICE'));
          }

          if (
            !customerResult.some(
              (customer) => customer.id === customerIdNum,
            )
          ) {
            const extra = await api.getCustomer(customerIdNum);
            if (cancelled) return;
            setCustomers([
              ...customerResult,
              {
                id: extra.id,
                name: extra.name,
                active: extra.active,
                creditDays: extra.creditDays,
              },
            ]);
          }

          setForm({
            ...emptyForm(),
            customerId: presetCustomerId,
            lines,
            allocations: presetInvoiceId
              ? [
                  {
                    ...createEmptyAllocation(),
                    invoiceId: presetInvoiceId,
                    invoiceAmount,
                    cashAmount,
                  },
                ]
              : [],
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, presetCustomerId, presetInvoiceId, labels.invoice]);

  function updateField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleCustomerChange(customerId: string) {
    updateField('customerId', customerId);
    updateField('allocations', []);
    if (!customerId) {
      setInvoices([]);
      return;
    }
    try {
      const result = await api.getInvoices({
        customerId: Number(customerId),
        pageSize: 100,
        status: 'unpaid',
      });
      setInvoices(result.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (!form.customerId) {
      setError(labels.requiredCustomer);
      return;
    }
    if (!form.paymentDate) {
      setError(labels.requiredPaymentDate);
      return;
    }

    const validLines = form.lines.filter(
      (line) => line.amount.trim() !== '' || line.description.trim() !== '',
    );
    if (validLines.length === 0) {
      setError(labels.requiredLines);
      return;
    }
    for (const line of validLines) {
      const amount = parseAmountInput(line.amount);
      if (!line.amount.trim() || !Number.isFinite(amount) || amount <= 0) {
        setError(t().common.amountMustBePositive);
        return;
      }
    }

    const validAllocations = form.allocations.filter(
      (row) =>
        row.invoiceId &&
        ((parseAmountInput(row.invoiceAmount) || 0) > 0 ||
          (parseAmountInput(row.cashAmount) || 0) > 0),
    );

    for (const row of validAllocations) {
      if (!row.invoiceId) {
        setError(labels.selectInvoice);
        return;
      }
    }

    if (allocationExceedsPayment) {
      setError(labels.allocationExceedsPayment);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        customerId: Number(form.customerId),
        paymentDate: form.paymentDate,
        description: form.description.trim() || undefined,
        notes: form.notes.trim() || undefined,
        allowOverpayment: form.allowOverpayment,
        allocations: validAllocations.map((row) => ({
          invoiceId: Number(row.invoiceId),
          invoiceAmount: amountToApiString(row.invoiceAmount || '0'),
          cashAmount: amountToApiString(row.cashAmount || '0'),
        })),
        lines: validLines.map((line, index) => ({
          type: line.type,
          amount: amountToApiString(line.amount),
          description: line.description.trim() || undefined,
          sortOrder: index,
        })),
      };

      const saved = isEdit
        ? await api.updatePayment(Number(id), payload)
        : await api.createPayment(payload);

      navigate(`/payments/${saved.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'OVERPAYMENT') {
        setError(err.message);
      } else {
        setError(err instanceof ApiError ? err.message : t().toasts.saveError);
      }
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <LoadingSpinner message={t().common.loading} size="small" />;
  }

  const customerOptions = customers.map((customer) => ({
    value: String(customer.id),
    label: customer.name,
  }));

  return (
    <FormPageShell
      onSubmit={handleSubmit}
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              navigate(isEdit ? `/payments/${id}` : '/payments')
            }
          >
            <XIcon className="h-4 w-4" />
            {t().common.cancel}
          </Button>
          <Button type="submit" loading={saving}>
            <SaveIcon className="h-4 w-4" />
            {t().common.save}
          </Button>
        </>
      }
    >
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-ink">
          {isEdit ? labels.edit : labels.new}
        </h2>
        <BackButton fallback={isEdit && id ? `/payments/${id}` : '/payments'} />
      </div>

      {error ? <Callout variant="danger">{error}</Callout> : null}

      <Card className="space-y-4 p-5">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label required>{labels.customer}</Label>
            <Select
              value={form.customerId}
              options={customerOptions}
              placeholder={labels.selectCustomer}
              showClearButton
              onChange={(e) => void handleCustomerChange(e.target.value)}
            />
          </div>
          <div>
            <Label required>{labels.paymentDate}</Label>
            <DateInput
              value={form.paymentDate}
              onChange={(e) => updateField('paymentDate', e.target.value)}
              showClearButton={false}
            />
          </div>
        </div>
        <div>
          <Label>{labels.description}</Label>
          <Textarea
            value={form.description}
            onChange={(e) => updateField('description', e.target.value)}
            rows={2}
          />
        </div>
        <div>
          <Label>{labels.notes}</Label>
          <Textarea
            value={form.notes}
            onChange={(e) => updateField('notes', e.target.value)}
            rows={2}
          />
        </div>
      </Card>

      <Card className="space-y-4 p-5">
        <h3 className="text-sm font-semibold text-ink">{labels.linesSection}</h3>
        <FinancialLinesEditor
          lines={form.lines}
          onChange={(lines) => updateField('lines', lines)}
          disabled={saving}
        />
        <div className="space-y-1 text-right text-sm">
          <div>
            {labels.invoicePayments}: {formatMoney(lineTotals.invoice)}
          </div>
          <div>
            {labels.cashPayments}: {formatMoney(lineTotals.cash)}
          </div>
          <div className="font-semibold">
            {labels.paymentTotal}: {formatMoney(lineTotals.total)}
          </div>
        </div>
      </Card>

      <Card className="space-y-4 p-5">
        <PaymentAllocationsEditor
          allocations={form.allocations}
          onChange={(allocations) => updateField('allocations', allocations)}
          invoices={invoices.map((invoice) => ({
            id: invoice.id,
            invoiceNo: invoice.invoiceNo,
            label: `${invoice.invoiceNo} · ${formatMoney(invoice.outstanding.total)} ${t().common.remainingLabel}`,
          }))}
          disabled={saving || !form.customerId}
          paymentInvoiceTotal={lineTotals.invoice}
          paymentCashTotal={lineTotals.cash}
        />
        {form.allocations.length === 0 && form.customerId ? (
          <div className="flex flex-wrap items-center gap-2">
            <Callout variant="warning" className="flex-1">
              {labels.unallocated}
            </Callout>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!form.customerId || invoices.length === 0}
              onClick={() =>
                updateField('allocations', [createEmptyAllocation()])
              }
            >
              <LinkChainIcon className="h-3.5 w-3.5" />
              {labels.addAllocation}
            </Button>
          </div>
        ) : null}
      </Card>

      <Callout variant="info">
        <div className="space-y-2">
          <div>{labels.overpaymentWarning}</div>
          <Switch
            checked={form.allowOverpayment}
            onChange={(e) =>
              updateField('allowOverpayment', e.target.checked)
            }
            label={labels.allowOverpayment}
          />
        </div>
      </Callout>
    </FormPageShell>
  );
}
